import { useMemo, useRef } from 'react';
import * as THREE from 'three';
import { useFrame } from '@react-three/fiber';
import { lerp, easeInOut } from '../../lab/shared/PouringCylinder';
import { MagnesiumRibbon } from '../../lab/shared/MagnesiumRibbon';
import { CrucibleLid } from '../../lab/shared/CrucibleLid';
import { Tongs } from '../../lab/shared/Tongs';
import { Sandpaper } from '../../lab/shared/Sandpaper';

/**
 * Step-specific animations for the empirical-formula experiment. Each
 * animation follows the same pattern as Solubility's PourStreams: a
 * boolean `active` plus a `progress` value (0..1) drives the entire
 * motion via `lerp` + `easeInOut`.
 */

interface BaseProps {
  active: boolean;
  progress: number;
}

const SANDPAPER_REST: [number, number, number] = [-1.2, -0.5, 0.2];
const RIBBON_FLAT_POS: [number, number, number] = [-1.2, -0.48, 0.2];
// Crucible positions in the heating-apparatus group's LOCAL frame (the group is
// itself at world [0, -0.585, 0]; local y=0 ≈ bench surface in world).
const CRUCIBLE_LOCAL: [number, number, number] = [0, 1.5 * 0.6 + 0.05, -0.2];
const CRUCIBLE_BENCH_POS: [number, number, number] = [0.45, 0.05, 0.3];
const CRUCIBLE_ON_BALANCE_POS: [number, number, number] = [1.0, 0.13, 0.44];
// World-space offset of the heating-apparatus group. Used to convert local
// crucible positions to world coords when the polishing workbench's animations
// need to target the crucible.
const HEATING_GROUP_OFFSET: [number, number, number] = [0, -0.585, 0];
const LID_REST_POS: [number, number, number] = [0.32, -0.46, -0.2];

/**
 * Sandpaper polishing animation — pad slides back and forth across the
 * flat magnesium ribbon on the polishing workbench. Visible only while
 * `isPolishingMg` is true. The ribbon's polished progress is driven by
 * the store, so the colour shift happens automatically.
 */
export function PolishAnimation({ active, progress, ribbonLengthCm }: BaseProps & { ribbonLengthCm: number }) {
  if (!active) return null;
  const strokeT = Math.sin(progress * Math.PI * 6);
  const padX = SANDPAPER_REST[0] + strokeT * 0.05;
  const padY = SANDPAPER_REST[1] + 0.016;
  const padZ = SANDPAPER_REST[2];
  // Sandpaper tilts very slightly as the stroke peaks at each end
  const padTilt = Math.cos(progress * Math.PI * 6) * 0.08;

  return (
    <group>
      <MagnesiumRibbon
        position={RIBBON_FLAT_POS}
        state={progress >= 0.95 ? 'polished' : 'dull'}
        polishProgress={progress}
        lengthCm={ribbonLengthCm}
      />
      <Sandpaper
        position={[padX, padY, padZ]}
        rotation={[0, 0, padTilt]}
        size={[0.06, 0.008, 0.045]}
      />
    </group>
  );
}

/**
 * Coiled magnesium ribbon arcs from the sandpaper workbench into the
 * crucible. The coil is held above the workbench, rises through an arc
 * trajectory and drops into the bowl.
 */
export function PlaceMgAnimation({ active, progress, ribbonLengthCm }: BaseProps & { ribbonLengthCm: number }) {
  if (!active) return null;
  const t = easeInOut(progress);
  // Convert the crucible's bench position (heating-group local) into world
  // coords so the polishing-workbench arc lands inside the bowl.
  const crucibleWorldX = CRUCIBLE_BENCH_POS[0] + HEATING_GROUP_OFFSET[0];
  const crucibleWorldY = CRUCIBLE_BENCH_POS[1] + HEATING_GROUP_OFFSET[1];
  const crucibleWorldZ = CRUCIBLE_BENCH_POS[2] + HEATING_GROUP_OFFSET[2];
  const startPos = [RIBBON_FLAT_POS[0], RIBBON_FLAT_POS[1] + 0.05, RIBBON_FLAT_POS[2]];
  const endPos = [crucibleWorldX, crucibleWorldY - 0.02, crucibleWorldZ];
  const x = lerp(startPos[0], endPos[0], t);
  const z = lerp(startPos[2], endPos[2], t);
  const arc = Math.sin(t * Math.PI) * 0.35;
  const y = lerp(startPos[1], endPos[1], t) + arc;
  const visible = progress < 0.95;
  if (!visible) return null;

  return (
    <group>
      <MagnesiumRibbon
        position={[x, y, z]}
        state="polished"
        polishProgress={1}
        coiled
        lengthCm={ribbonLengthCm}
        rotation={[progress * Math.PI * 0.4, 0, 0]}
      />
    </group>
  );
}

/**
 * Drives the crucible's local-frame position (inside the heating-apparatus
 * group). Priority of motion sources, highest first:
 *  1. Weighing arc — bench/tripod → balance pan → bench/tripod
 *  2. Place-on-tripod arc — bench → pipeclay triangle
 *  3. apparatusReady — sit at the tripod position
 *  4. Default — sit at the bench position (visible on the workbench)
 *
 * `weighType` decides the weighing arc's "home" position: 'final' returns to
 * the tripod, the earlier weighings return to the bench.
 */
type WeighType = 'empty' | 'with-mg' | 'final' | null;
export function useCrucibleWorldPosition(
  isPlacingOnTripod: boolean,
  placeOnTripodProgress: number,
  isWeighing: boolean,
  weighProgress: number,
  weighType: WeighType,
  apparatusReady: boolean,
): [number, number, number] {
  if (isWeighing) {
    const home = weighType === 'final' ? CRUCIBLE_LOCAL : CRUCIBLE_BENCH_POS;
    if (weighProgress < 0.45) {
      // home → balance
      const t = easeInOut(weighProgress / 0.45);
      const x = lerp(home[0], CRUCIBLE_ON_BALANCE_POS[0], t);
      const z = lerp(home[2], CRUCIBLE_ON_BALANCE_POS[2], t);
      const arc = Math.sin(t * Math.PI) * 0.22;
      const y = lerp(home[1], CRUCIBLE_ON_BALANCE_POS[1], t) + arc;
      return [x, y, z];
    }
    if (weighProgress < 0.6) {
      // resting on the balance pan
      return CRUCIBLE_ON_BALANCE_POS;
    }
    // balance → home
    const t = easeInOut((weighProgress - 0.6) / 0.4);
    const x = lerp(CRUCIBLE_ON_BALANCE_POS[0], home[0], t);
    const z = lerp(CRUCIBLE_ON_BALANCE_POS[2], home[2], t);
    const arc = Math.sin(t * Math.PI) * 0.22;
    const y = lerp(CRUCIBLE_ON_BALANCE_POS[1], home[1], t) + arc;
    return [x, y, z];
  }

  if (isPlacingOnTripod) {
    const t = easeInOut(placeOnTripodProgress);
    const x = lerp(CRUCIBLE_BENCH_POS[0], CRUCIBLE_LOCAL[0], t);
    const z = lerp(CRUCIBLE_BENCH_POS[2], CRUCIBLE_LOCAL[2], t);
    const arc = Math.sin(t * Math.PI) * 0.4;
    const y = lerp(CRUCIBLE_BENCH_POS[1], CRUCIBLE_LOCAL[1], t) + arc;
    return [x, y, z];
  }

  if (apparatusReady) return CRUCIBLE_LOCAL;

  return CRUCIBLE_BENCH_POS;
}

/**
 * Crucible lid placement / lift animation. Computes the lid's vertical
 * lift offset and rotation so the lid floats above the crucible during
 * placement (descending) or lifts upward at an angle (during the brief
 * lift to let air in).
 */
export function useLidTransform(
  isPlacingLid: boolean,
  lidPlaceProgress: number,
  isLiftingLid: boolean,
  lidLiftProgress: number,
  lidIsOn: boolean,
): { liftHeight: number; tiltZ: number; tongsVisible: boolean; tongsPos: [number, number, number]; tongsTilt: number } {
  if (isPlacingLid) {
    const t = easeInOut(lidPlaceProgress);
    const liftHeight = lerp(0.35, 0, t);
    return {
      liftHeight,
      tiltZ: lerp(0.4, 0, t),
      tongsVisible: true,
      tongsPos: [0.06, CRUCIBLE_LOCAL[1] + 0.4 - t * 0.3, CRUCIBLE_LOCAL[2] - 0.02],
      tongsTilt: lerp(0.3, 0, t),
    };
  }
  if (isLiftingLid) {
    // 0..0.28: lift up & tilt
    // 0.28..0.72: hold open (peak)
    // 0.72..1: descend back onto crucible
    let p = lidLiftProgress;
    let phaseT: number;
    if (p < 0.28) phaseT = easeInOut(p / 0.28);
    else if (p < 0.72) phaseT = 1;
    else phaseT = easeInOut(1 - (p - 0.72) / 0.28);
    const liftHeight = phaseT * 0.18;
    const tiltZ = phaseT * 0.55;
    return {
      liftHeight,
      tiltZ,
      tongsVisible: true,
      tongsPos: [-0.02, CRUCIBLE_LOCAL[1] + 0.08 + liftHeight, CRUCIBLE_LOCAL[2]],
      tongsTilt: tiltZ * 0.6,
    };
  }
  if (!lidIsOn) {
    // Lid is off — render somewhere out of the way to the side
    return {
      liftHeight: -2,
      tiltZ: 0,
      tongsVisible: false,
      tongsPos: [0, 0, 0],
      tongsTilt: 0,
    };
  }
  return { liftHeight: 0, tiltZ: 0, tongsVisible: false, tongsPos: [0, 0, 0], tongsTilt: 0 };
}

/**
 * Ignition flash — bright white sphere expands and fades at the moment
 * the magnesium ribbon catches fire. Also emits small sparks via an
 * instanced mesh.
 */
const SPARK_COUNT = 32;
export function IgnitionFlash({ active, progress, position }: BaseProps & { position: [number, number, number] }) {
  const sparkRef = useRef<THREE.InstancedMesh>(null);
  const dummy = useMemo(() => new THREE.Object3D(), []);
  const seeds = useMemo(() => Array.from({ length: SPARK_COUNT }, () => ({
    dir: new THREE.Vector3(
      Math.random() - 0.5,
      Math.random() * 0.6 + 0.5,
      Math.random() - 0.5,
    ).normalize(),
    speed: 0.4 + Math.random() * 0.6,
    spin: Math.random() * Math.PI * 2,
  })), []);

  useFrame((state) => {
    if (!sparkRef.current) return;
    if (!active || progress > 0.85) {
      for (let i = 0; i < SPARK_COUNT; i++) {
        dummy.position.set(0, -999, 0);
        dummy.scale.setScalar(0);
        dummy.updateMatrix();
        sparkRef.current.setMatrixAt(i, dummy.matrix);
      }
      sparkRef.current.instanceMatrix.needsUpdate = true;
      return;
    }
    const life = Math.min(1, progress * 1.6);
    for (let i = 0; i < SPARK_COUNT; i++) {
      const s = seeds[i];
      const dist = s.speed * life * 0.4;
      dummy.position.set(
        position[0] + s.dir.x * dist,
        position[1] + s.dir.y * dist,
        position[2] + s.dir.z * dist,
      );
      const size = 0.006 * (1 - life);
      dummy.scale.setScalar(Math.max(0, size));
      dummy.rotation.set(state.clock.elapsedTime * 2 + s.spin, state.clock.elapsedTime, 0);
      dummy.updateMatrix();
      sparkRef.current.setMatrixAt(i, dummy.matrix);
    }
    sparkRef.current.instanceMatrix.needsUpdate = true;
  });

  if (!active) return null;

  // Bright flash for the first ~0.4 of the progress, then a steady combustion glow
  const flashStrength = progress < 0.4 ? (1 - progress / 0.4) : 0;
  const burnStrength = progress < 0.4 ? 0.6 : Math.max(0, 1 - (progress - 0.4) / 0.6);

  return (
    <group>
      {flashStrength > 0.01 && (
        <mesh position={position}>
          <sphereGeometry args={[0.06 + 0.05 * flashStrength, 24, 24]} />
          <meshBasicMaterial
            color="#ffffff"
            transparent
            opacity={flashStrength * 0.9}
            blending={THREE.AdditiveBlending}
            depthWrite={false}
          />
        </mesh>
      )}
      {burnStrength > 0.01 && (
        <pointLight
          position={[position[0], position[1] + 0.05, position[2]]}
          intensity={burnStrength * 4}
          color="#fff6e6"
          distance={1.2}
          decay={2}
        />
      )}
      <instancedMesh ref={sparkRef} args={[undefined, undefined, SPARK_COUNT]}>
        <boxGeometry args={[1, 1, 1]} />
        <meshBasicMaterial color="#ffeec0" transparent opacity={0.9} blending={THREE.AdditiveBlending} depthWrite={false} />
      </instancedMesh>
    </group>
  );
}

/**
 * White fumes — small rising particles emitted when the lid is lifted.
 * Driven by lidLiftProgress; particles spawn during the "open" window
 * (0.28..0.72) and continue floating up briefly after the lid closes.
 */
const FUME_COUNT = 50;
export function WhiteFumes({
  active,
  position,
}: {
  active: boolean;
  position: [number, number, number];
}) {
  const ref = useRef<THREE.InstancedMesh>(null);
  const dummy = useMemo(() => new THREE.Object3D(), []);
  const seeds = useMemo(() => Array.from({ length: FUME_COUNT }, () => ({
    speed: 0.18 + Math.random() * 0.22,
    phase: Math.random(),
    sway: (Math.random() - 0.5) * 0.04,
    swayPhase: Math.random() * Math.PI * 2,
    size: 0.03 + Math.random() * 0.025,
  })), []);

  useFrame((state) => {
    if (!ref.current) return;
    if (!active) {
      for (let i = 0; i < FUME_COUNT; i++) {
        dummy.position.set(0, -999, 0);
        dummy.scale.setScalar(0);
        dummy.updateMatrix();
        ref.current.setMatrixAt(i, dummy.matrix);
      }
      ref.current.instanceMatrix.needsUpdate = true;
      return;
    }
    const t = state.clock.elapsedTime;
    for (let i = 0; i < FUME_COUNT; i++) {
      const s = seeds[i];
      const cycle = (t * s.speed + s.phase) % 1;
      const y = position[1] + cycle * 0.6;
      const x = position[0] + Math.sin(t * 1.5 + s.swayPhase) * s.sway;
      const z = position[2] + Math.cos(t * 1.4 + s.swayPhase) * s.sway;
      const fade = 1 - cycle;
      dummy.position.set(x, y, z);
      dummy.scale.setScalar(s.size * fade);
      dummy.rotation.set(t * 0.3 + s.swayPhase, t * 0.4, 0);
      dummy.updateMatrix();
      ref.current.setMatrixAt(i, dummy.matrix);
    }
    ref.current.instanceMatrix.needsUpdate = true;
  });

  if (!active) return null;
  return (
    <instancedMesh ref={ref} args={[undefined, undefined, FUME_COUNT]}>
      <sphereGeometry args={[1, 8, 8]} />
      <meshBasicMaterial
        color="#fafafa"
        transparent
        opacity={0.45}
        depthWrite={false}
      />
    </instancedMesh>
  );
}

/**
 * Tongs floating into the scene during lid placement / lid lift. Tongs
 * fade away when not active.
 */
export function AnimatedTongs({
  visible,
  position,
  tilt,
  openAmount,
}: {
  visible: boolean;
  position: [number, number, number];
  tilt: number;
  openAmount: number;
}) {
  if (!visible) return null;
  return (
    <group position={position} rotation={[0, 0, tilt]}>
      <Tongs openAmount={openAmount} />
    </group>
  );
}

/**
 * Heat shimmer wisps over the crucible while it glows red — subtle hot
 * air distortion using a few thin transparent meshes.
 */
export function HeatShimmer({
  visible,
  position,
}: {
  visible: boolean;
  position: [number, number, number];
}) {
  const groupRef = useRef<THREE.Group>(null);
  useFrame((state) => {
    if (!groupRef.current) return;
    const t = state.clock.elapsedTime;
    groupRef.current.children.forEach((child, i) => {
      const m = child as THREE.Mesh;
      m.position.y = position[1] + 0.08 + ((t * 0.3 + i * 0.15) % 0.4);
      m.position.x = position[0] + Math.sin(t * 1.5 + i) * 0.02;
      const fade = 1 - ((t * 0.3 + i * 0.15) % 0.4) / 0.4;
      const mat = m.material as THREE.MeshBasicMaterial;
      if (mat && mat.opacity !== undefined) mat.opacity = fade * 0.18;
    });
  });
  if (!visible) return null;
  return (
    <group ref={groupRef}>
      {[0, 1, 2, 3].map((i) => (
        <mesh key={i} position={[position[0], position[1] + 0.1, position[2]]}>
          <sphereGeometry args={[0.04, 8, 8]} />
          <meshBasicMaterial color="#fff0d8" transparent opacity={0.15} depthWrite={false} />
        </mesh>
      ))}
    </group>
  );
}

export { CrucibleLid, Tongs };
